import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';

const COLORS = ['#2563eb', '#16a34a', '#7c3aed', '#dc2626', '#ca8a04', '#db2777', '#4680FF'];

const formatMoney = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function ClientPaymentGatewayDashboard(props) {
  const [tabFilter, setTabFilter] = React.useState('');
  const [appliedTabFilter, setAppliedTabFilter] = React.useState('');
  const [coordinatorFilter, setCoordinatorFilter] = React.useState('');
  const [appliedCoordinator, setAppliedCoordinator] = React.useState('');
  const [tabs, setTabs] = React.useState([]);
  const [coordinatorOptions, setCoordinatorOptions] = React.useState([]);
  const [dashboardData, setDashboardData] = React.useState({
    totals: {},
    byMonth: [],
    byMode: [],
    byCoordinator: [],
    latestTabName: '',
    tabs: [],
    last_sync_time: null,
  });
  const [loading, setLoading] = React.useState(false);

  const fetchCoordinatorOptions = React.useCallback(() => {
    getRequest(
      GET_URL.client_payment_gateway_report.api,
      { coordinator_options: 1 },
      props
    ).then((response) => {
      if (response?.status === 200) {
        setCoordinatorOptions(response.data?.data || []);
      }
    }).catch(() => {});
  }, [props]);

  React.useEffect(() => {
    fetchCoordinatorOptions();
  }, [fetchCoordinatorOptions]);

  const fetchDashboard = React.useCallback(() => {
    setLoading(true);
    getRequest(
      GET_URL.client_payment_gateway_report.api,
      {
        dashboard: 1,
        ...(appliedTabFilter && { tab_name: appliedTabFilter }),
        ...(appliedCoordinator && { coordinator: appliedCoordinator }),
      },
      props
    ).then((response) => {
      setLoading(false);
      if (response?.status === 200) {
        const d = response.data?.data || {};
        setDashboardData(d);
        setTabs(d.tabs || []);
      }
    }).catch(() => setLoading(false));
  }, [appliedCoordinator, appliedTabFilter, props]);

  React.useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const totals = dashboardData.totals || {};
  const byMonth = (dashboardData.byMonth || []).map((row) => ({
    ...row,
    label: row.year_month_label || row.year_month || '',
  }));
  const byMode = (dashboardData.byMode || []).map((row) => ({
    ...row,
    name: row.mode_of_payment || '—',
  }));
  const byCoordinator = dashboardData.byCoordinator || [];

  return (
    <Box p={2}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Typography variant="h6" fontWeight={700}>Payment Gateway Dashboard</Typography>
          <Typography variant="body2" color="text.secondary">
            Aggregates from stored month rows (summary + modes in JSON). Filters apply to all charts.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last data update: {dashboardData.last_sync_time ? new Date(dashboardData.last_sync_time).toLocaleString() : 'No data yet'}
            {dashboardData.latestTabName ? ` · Active tab filter context: ${dashboardData.latestTabName}` : ''}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Report date"
              value={tabFilter}
              onChange={(e) => setTabFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All</MenuItem>
              {(tabs.length ? tabs : dashboardData.tabs || []).map((tab) => (
                <MenuItem key={tab} value={tab}>{tab}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Institute POC (from client report)"
              value={coordinatorFilter}
              onChange={(e) => setCoordinatorFilter(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {coordinatorOptions.map((opt) => (
                <MenuItem key={opt.id} value={String(opt.id)}>{opt.name || opt.id}</MenuItem>
              ))}
            </TextField>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={() => {
                setAppliedTabFilter(tabFilter);
                setAppliedCoordinator(coordinatorFilter);
              }}
            >
              Apply
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <MetricCard label="Total amount" value={formatMoney(totals.total_amount)} color="#1665D8" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard label="Transactions (count)" value={String(totals.transaction_count ?? 0)} color="#16a34a" />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricCard label="Transaction fees" value={formatMoney(totals.fees)} color="#7c3aed" />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <ChartCard title="Amount by month">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={byMonth} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => formatMoney(v)} />
                <Tooltip formatter={(value) => formatMoney(value)} />
                <Bar dataKey="total_amount" name="Amount" fill="#4680FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={5}>
          <ChartCard title="Amount by payment mode">
            {byMode.length ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={byMode}
                    dataKey="total_amount"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={90}
                    paddingAngle={3}
                  >
                    {byMode.map((entry, index) => (
                      <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatMoney(value)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No mode breakdown for current filters.</Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard title="Amount by coordinator">
            {byCoordinator.length ? (
              <ResponsiveContainer width="100%" height={Math.max(240, byCoordinator.length * 36)}>
                <BarChart
                  data={byCoordinator.map((row) => ({
                    ...row,
                    label: row.coordinator_name || String(row.coordinator_id) || '—',
                  }))}
                  layout="vertical"
                  margin={{ top: 10, right: 30, left: 12, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tickFormatter={(v) => formatMoney(v)} />
                  <YAxis dataKey="label" type="category" width={160} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatMoney(value)} />
                  <Bar dataKey="total_amount" name="Amount" fill="#4680FF" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">No coordinator-linked rows (map companies to coordinators in billing setup).</Typography>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Monthly detail</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Transactions</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Fees</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byMonth.length ? byMonth.map((row) => (
                    <TableRow key={row.year_month}>
                      <TableCell>{row.year_month_label || row.year_month}</TableCell>
                      <TableCell align="right">{row.transaction_count ?? 0}</TableCell>
                      <TableCell align="right">{formatMoney(row.total_amount)}</TableCell>
                      <TableCell align="right">{formatMoney(row.fees)}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">No rows for current filters.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

const MetricCard = ({ label, value, color = '#4680FF' }) => (
  <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3 }}>
    <CardContent>
      <Typography variant="overline" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={800} color={color}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 3, height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
        {title}
      </Typography>
      {children}
    </CardContent>
  </Card>
);

export default ClientPaymentGatewayDashboard;
